import { Component, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { RegisterRequest } from '../../models/registerRequest';
import { UserService } from '../../services/user.service';
import { UserDataService } from '../../services/user-data.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AbstractControlOptions, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Country } from '../../models/country';
import { MessageComponent } from '../../modals/message/message.component';
import { switchMap } from 'rxjs';
import { UpperCasePipe } from '@angular/common';
import { ModalService } from '../../services/modal.service';
import { Utils } from '../../services/utils.service';

@Component({
  selector: 'app-register',
  imports: [TranslatePipe, FormsModule, ReactiveFormsModule, UpperCasePipe],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {
  
  clickFirst = false;
  clickSecond = false;
  clickThird = false;
  countries = signal<Array<Country>>([]);
  passwordVisible = false;
  confirmPasswordVisible = false;

  registerForm!: FormGroup;
  submitted = false;

  constructor(private router: Router,
    private userService: UserService,
    private spinner: NgxSpinnerService,
    private userDataService: UserDataService,
    private translate: TranslateService,
    private fb: FormBuilder,
    private modalService: ModalService,
    private utils: Utils,
  ) {}

  ngOnInit(): void {
    this.registerForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      confirmEmail: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      confirmPassword: ['', [Validators.required]],
      country: ['', [Validators.required]],
    }, {
      validators: [this.checkEmails, this.checkPasswords]
    } as AbstractControlOptions);

    this.userService.getCountries().subscribe({
      next: page => {
        this.countries.set(page.records);
        this.registerForm.get('country')?.setValue(page.records[0].code);
      }
    });
  }

  clickCheckbox1() {
    this.clickFirst = !this.clickFirst;
  }

  clickCheckbox2() {
    this.clickSecond = !this.clickSecond;
  }

  clickCheckbox3() {
    this.clickThird = !this.clickThird;
  }

  onSubmit() {
    this.submitted = true;
    if (!this.registerForm.valid) {
      return;
    }
    if (!this.clickFirst || !this.clickSecond) {
      const modal = this.modalService.show(MessageComponent);
      modal.componentInstance.message.set(this.translate.instant('error_must_accept_contracts'));
      return;
    }

    const userRegister: RegisterRequest = {
      firstName: this.registerForm.get('firstName')?.value,
      lastName: this.registerForm.get('lastName')?.value,
      email: this.registerForm.get('email')?.value,
      password: this.registerForm.get('password')?.value,
      news: this.clickThird,
      country: this.registerForm.get('country')?.value,
    };
    this.spinner.show();
    this.userService.registerUser(userRegister).pipe(
      switchMap(response => {
        localStorage.setItem('token', response.accessToken);
        return this.userDataService.checkUser();
      })
    ).subscribe({
      next: _ => {
        this.spinner.hide();
        this.router.navigate(['/feedback']);
      },
      error: error => {
        this.spinner.hide();
        this.utils.showMessage(error, this.translate.instant('message_register_error'));
      }
    });
  }

  private checkEmails(fg: FormGroup): ValidationErrors | null {
    const email = fg.get('email')?.value;
    const confirmEmail = fg.get('confirmEmail')?.value;
    if (!email || !confirmEmail) {
      return null;  
    }
    if (email != confirmEmail) {
      return {
        notSameEmail: true
      }
    }
    return null;
  }

  private checkPasswords(fg: FormGroup): ValidationErrors | null {
    const regexp = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*\(\)_\-+=\{\}\[\]|:;"'<>,.\?/~`\\])[a-zA-Z0-9!@#$%^&*\(\)_\-+=\{\}\[\]|:;"'<>,.\?/~`\\]{12,}$/;
    const firstName = fg.get('firstName')?.value ?? '';
    const lastName = fg.get('lastName')?.value ?? '';
    const password = fg.get('password')?.value;
    const confirmPassword = fg.get('confirmPassword')?.value;
    if (!password || !confirmPassword) {
      return null;  
    }

    if (!password || !password.match(regexp)) {
      return {
        invalidPassword: true
      }
    }

    if (password && password.length > 0 && firstName && firstName.length > 0 
      && password.toLowerCase().indexOf(firstName.toLowerCase()) !== -1) {
      return {
        invalidPassword: true
      }
    }

    if (password && password.length > 0 && lastName && lastName.length > 0 
      && password.toLowerCase().indexOf(lastName.toLowerCase()) !== -1) {
      return {
        invalidPassword: true
      }
    }

    if (password !== confirmPassword) {
      return {
        notSamePassword: true
      }
    }

    return null;
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  toggleConfirmPasswordVisibility() {
    this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }
}
